import { Quaternion, Vector3 } from '@technology/web3d';
import type { SmoothedCamera } from './camera-rotation-smoother.types';

const DEFAULT_DURATION_MS = 150;

// eslint-disable-next-line no-magic-numbers -- smooth-step polynomial coefficients
const smoothStep = (t: number): number => t * t * (3 - 2 * t);

const backwardOf = (quaternion: Quaternion): Vector3 =>
  new Vector3(0, 0, 1).applyQuaternion(quaternion);

/**
 * Eases the camera rotation towards a target when the alignment turns, while keeping
 * the pivot (center marker) at the center of the screen at a fixed distance.
 */
export class CameraRotationSmoother {
  readonly #getCamera: () => SmoothedCamera;
  readonly #getPivot: () => Vector3;
  readonly #durationMs: number;
  readonly #startQuaternion = new Quaternion();
  readonly #endQuaternion = new Quaternion();
  readonly #endPosition = new Vector3();
  #distance = 0;
  #elapsedMs = 0;
  #active = false;

  constructor(
    getCamera: () => SmoothedCamera,
    getPivot: () => Vector3,
    durationMs = DEFAULT_DURATION_MS
  ) {
    this.#getCamera = getCamera;
    this.#getPivot = getPivot;
    this.#durationMs = durationMs;
  }

  public get active(): boolean {
    return this.#active;
  }

  /** Where the camera rotation is heading: the target while smoothing, else the current one. */
  public get targetQuaternion(): Quaternion {
    return this.#active ? this.#endQuaternion : this.#getCamera().quaternion;
  }

  public get targetPosition(): Vector3 {
    return this.#active ? this.#endPosition : this.#getCamera().position;
  }

  public get targetDistance(): number {
    return this.#active
      ? this.#distance
      : this.#getCamera().position.distanceTo(this.#getPivot());
  }

  public start(
    endQuaternion: Quaternion,
    endPosition: Vector3,
    distance: number
  ): void {
    this.#startQuaternion.copy(this.#getCamera().quaternion);
    this.#endQuaternion.copy(endQuaternion);
    this.#endPosition.copy(endPosition);
    this.#distance = distance;
    this.#elapsedMs = 0;
    this.#active = true;
    this.#placeCamera();
  }

  /** Changes the pivot distance of an ongoing smoothing; ignored when inactive. */
  public setDistance(distance: number): void {
    if (!this.#active) return;
    this.#distance = distance;
    this.retarget();
    this.#placeCamera();
  }

  /** Follows a pivot moved during smoothing (e.g. by panning); ignored when inactive. */
  public retarget(): void {
    if (!this.#active) return;
    this.#endPosition
      .copy(this.#getPivot())
      .addScaledVector(backwardOf(this.#endQuaternion), this.#distance);
  }

  public cancel(): void {
    this.#active = false;
  }

  /** Advances by `deltaMs`; returns whether the camera moved. */
  public update(deltaMs: number): boolean {
    if (!this.#active) return false;
    this.#elapsedMs += deltaMs;
    const t = Math.min(1, this.#elapsedMs / this.#durationMs);
    if (t >= 1) {
      this.#active = false;
      this.#getCamera().quaternion.copy(this.#endQuaternion);
    } else {
      this.#getCamera().quaternion.slerpQuaternions(
        this.#startQuaternion,
        this.#endQuaternion,
        smoothStep(t)
      );
    }
    this.#placeCamera();
    return true;
  }

  #placeCamera(): void {
    const camera = this.#getCamera();
    camera.position
      .copy(this.#getPivot())
      .addScaledVector(backwardOf(camera.quaternion), this.#distance);
  }
}
